import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  DataEditor,
  GridCellKind,
  type GridCell,
  type GridColumn,
  type Item,
} from "@glideapps/glide-data-grid";
import "@glideapps/glide-data-grid/dist/index.css";
import { fetchJson } from "../../lib/api";
import { getAccountSignExpectation } from "../../lib/account-flow";
import { formatAmountValue } from "../../lib/format-utils";
import type { BudgetWorksheetResponse } from "../../lib/types";
import { useI18n } from "../../i18n/i18n";
import { Button } from "../button";
import { Card, CardContent, CardHeader, CardTitle } from "../card";
import { Input } from "../input";

type GridBudgetRow = Omit<BudgetWorksheetResponse["rows"][number], "budgetByYear"> & {
  budgetByYear: Record<number, number | null>;
};

const buildCellKey = (accountId: number, year: number) => `${accountId}:${year}`;
const buildRowData = (rows: BudgetWorksheetResponse["rows"]) =>
  rows
    .map<GridBudgetRow>((row) => ({
      ...row,
      budgetByYear: Object.fromEntries(
        Object.entries(row.budgetByYear).map(([key, value]) => [Number(key), value]),
      ) as Record<number, number | null>,
    }))
    .sort((left, right) => left.number.localeCompare(right.number, "da"));

const parseBudgetCellValue = (value: number) => (Number.isNaN(value) ? null : value);
const normalizeNumericInput = (value: string): string => {
  const trimmed = value.trim().replace(/\s/g, "");
  if (!trimmed) {
    return "";
  }
  const hasComma = trimmed.includes(",");
  const hasDot = trimmed.includes(".");
  if (hasComma && hasDot) {
    const lastComma = trimmed.lastIndexOf(",");
    const lastDot = trimmed.lastIndexOf(".");
    const decimalSep = lastComma > lastDot ? "," : ".";
    const thousandsSep = decimalSep === "," ? "." : ",";
    return trimmed.replaceAll(thousandsSep, "").replace(decimalSep, ".");
  }
  if (hasComma) {
    if (/,\d{1,2}$/.test(trimmed)) {
      return trimmed.replace(",", ".");
    }
    return trimmed.replaceAll(",", "");
  }
  if (hasDot) {
    if (/\.\d{1,2}$/.test(trimmed)) {
      return trimmed;
    }
    return trimmed.replaceAll(".", "");
  }
  return trimmed;
};

const parsePastedValue = (value: string): number | null | undefined => {
  const normalized = normalizeNumericInput(value);
  if (!normalized) {
    return null;
  }
  const parsed = Number.parseFloat(normalized);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const BUDGET_GRID_ROW_HEIGHT = 28;
const BUDGET_GRID_HEADER_HEIGHT = 30;

export const BudgetWorksheetView = ({
  houseId,
  search,
  setSearch,
  houseRole,
}: {
  houseId: number | null;
  search: string;
  setSearch: (nextSearch: string, options?: { replace?: boolean }) => void;
  houseRole: "readonly" | "full";
}) => {
  const { t } = useI18n();
  const searchParams = useMemo(() => new URLSearchParams(search), [search]);
  const startYearParam = Number.parseInt(searchParams.get("startYear") ?? "", 10);
  const actualYearParam = Number.parseInt(searchParams.get("actualYear") ?? "", 10);
  const [report, setReport] = useState<BudgetWorksheetResponse | null>(null);
  const [rowData, setRowData] = useState<GridBudgetRow[]>([]);
  const [dirtyKeys, setDirtyKeys] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const originalBudgetsRef = useRef<Map<string, number>>(new Map());

  const years = report?.years ?? [];
  const startYear = report?.startYear ?? new Date().getUTCFullYear();
  const actualYear = report?.actualYear ?? startYear;
  const reportCurrencies = useMemo(() => {
    const currencies = new Set<string>();
    rowData.forEach((row) => {
      if (row.currency) {
        currencies.add(row.currency);
      }
    });
    return Array.from(currencies).sort((left, right) => left.localeCompare(right, "en"));
  }, [rowData]);
  const currencyLabel = reportCurrencies.length ? reportCurrencies.join(", ") : "DKK";

  useEffect(() => {
    if (!houseId) {
      setReport(null);
      return;
    }
    const params = new URLSearchParams(search);
    if (!Number.isNaN(startYearParam)) {
      params.set("startYear", String(startYearParam));
    }
    if (!Number.isNaN(actualYearParam)) {
      params.set("actualYear", String(actualYearParam));
    }
    const query = params.toString() ? `?${params.toString()}` : "";
    setIsLoading(true);
    setError(null);
    fetchJson<BudgetWorksheetResponse>(`/api/houses/${houseId}/budget-worksheet${query}`)
      .then((response) => {
        setReport(response);
        const nextParams = new URLSearchParams(search);
        if (response.startYear !== startYearParam) {
          nextParams.set("startYear", String(response.startYear));
        }
        if (response.actualYear !== actualYearParam) {
          nextParams.set("actualYear", String(response.actualYear));
        }
        const nextSearch = `?${nextParams.toString()}`;
        if (nextSearch !== search) {
          setSearch(nextSearch, { replace: true });
        }
      })
      .catch((loadError) => {
        setError(
          loadError instanceof Error
            ? loadError.message
            : t("reports.budgetWorksheetLoadFailed", "Failed to load budget worksheet"),
        );
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [houseId, search, setSearch, startYearParam, actualYearParam, t]);

  useEffect(() => {
    if (!report) {
      setRowData([]);
      originalBudgetsRef.current = new Map();
      setDirtyKeys(new Set());
      return;
    }
    const nextRows = buildRowData(report.rows);
    const originalMap = new Map<string, number>();
    report.rows.forEach((row) => {
      years.forEach((year) => {
        originalMap.set(buildCellKey(row.accountId, year), row.budgetByYear[year] ?? 0);
      });
    });
    originalBudgetsRef.current = originalMap;
    setRowData(nextRows);
    setDirtyKeys(new Set());
  }, [report, years]);

  const updateStartYear = (value: string) => {
    const parsed = Number.parseInt(value, 10);
    if (!Number.isFinite(parsed)) {
      return;
    }
    const nextParams = new URLSearchParams(search);
    nextParams.set("startYear", String(parsed));
    const nextSearch = `?${nextParams.toString()}`;
    if (nextSearch !== search) {
      setSearch(nextSearch);
    }
  };

  const updateActualYear = (value: string) => {
    const parsed = Number.parseInt(value, 10);
    if (!Number.isFinite(parsed)) {
      return;
    }
    const nextParams = new URLSearchParams(search);
    nextParams.set("actualYear", String(parsed));
    const nextSearch = `?${nextParams.toString()}`;
    if (nextSearch !== search) {
      setSearch(nextSearch);
    }
  };

  const handleBudgetChange = useCallback((accountId: number, year: number, value: number | null) => {
    const key = buildCellKey(accountId, year);
    const normalized = value ?? 0;
    const original = originalBudgetsRef.current.get(key) ?? 0;
    setDirtyKeys((prev) => {
      const next = new Set(prev);
      if (normalized !== original) {
        next.add(key);
      } else {
        next.delete(key);
      }
      return next;
    });
  }, []);

  const hasInvertedValues = useCallback(
    (row: GridBudgetRow) => {
      const expectation = getAccountSignExpectation({ flow: row.flow, number: row.number });
      if (!expectation) {
        return false;
      }
      return years.some((year) => {
        const value = row.budgetByYear[year] ?? 0;
        if (!Number.isFinite(value) || value === 0) {
          return false;
        }
        return Math.sign(value) !== expectation.expectedSign;
      });
    },
    [years],
  );

  const columns = useMemo<GridColumn[]>(() => {
    const baseColumns: GridColumn[] = [
      { title: t("accounts.account", "Account"), width: 70 },
      { title: t("accounts.name", "Name"), width: 200 },
      { title: "", width: 28 },
      { title: `${t("reports.actual", "Actual")} · ${actualYear}`, width: 100 },
    ];
    const budgetColumns: GridColumn[] = years.map((year) => ({
      title: String(year),
      width: 85,
    }));
    const actionColumn: GridColumn = { title: "", width: 28 };
    return [...baseColumns, ...budgetColumns, actionColumn];
  }, [years, actualYear, t]);

  const getCellContent = useCallback(
    ([col, row]: Item): GridCell => {
      const record = rowData[row];
      if (!record) {
        return {
          kind: GridCellKind.Loading,
          allowOverlay: false,
        };
      }

      if (col === 0) {
        return {
          kind: GridCellKind.Text,
          data: record.number,
          displayData: record.number,
          allowOverlay: false,
          readonly: true,
        };
      }
      if (col === 1) {
        return {
          kind: GridCellKind.Text,
          data: record.name,
          displayData: record.name,
          allowOverlay: false,
          readonly: true,
        };
      }
      if (col === 2) {
        const showWarning = hasInvertedValues(record);
        return {
          kind: GridCellKind.Text,
          data: showWarning ? "⚠" : "",
          displayData: showWarning ? "⚠" : "",
          allowOverlay: false,
          readonly: true,
        };
      }
      if (col === 3) {
        const actualValue = record.actual ?? 0;
        return {
          kind: GridCellKind.Number,
          data: actualValue,
          displayData: formatAmountValue(actualValue),
          allowOverlay: false,
          readonly: true,
        };
      }
      const yearIndex = col - 4;
      const year = years[yearIndex];
      if (year !== undefined) {
        const value = record.budgetByYear[year] ?? null;
        return {
          kind: GridCellKind.Number,
          data: value ?? undefined,
          displayData:
            value === null || value === undefined ? "" : formatAmountValue(value),
          allowOverlay: true,
          readonly: houseRole === "readonly",
        };
      }
      return {
        kind: GridCellKind.Text,
        data: "⇄",
        displayData: "⇄",
        allowOverlay: false,
        readonly: true,
      };
    },
    [rowData, years, houseRole, hasInvertedValues],
  );

  const handleCellEdited = useCallback(
    ([col, row]: Item, newValue: GridCell) => {
      if (col < 4) {
        return;
      }
      const yearIndex = col - 4;
      const year = years[yearIndex];
      if (!year) {
        return;
      }
      if (newValue.kind !== GridCellKind.Number) {
        return;
      }
      const parsedValue = parseBudgetCellValue(newValue.data ?? Number.NaN);
      setRowData((prev) => {
        const next = [...prev];
        const record = next[row];
        if (!record) {
          return prev;
        }
        next[row] = {
          ...record,
          budgetByYear: {
            ...record.budgetByYear,
            [year]: parsedValue,
          },
        };
        handleBudgetChange(record.accountId, year, parsedValue);
        return next;
      });
    },
    [handleBudgetChange, years],
  );

  const handlePaste = useCallback(
    (target: Item, values: readonly (readonly string[])[]) => {
      if (houseRole === "readonly") {
        return false;
      }
      if (values.length === 0) {
        return false;
      }
      const [startCol, startRow] = target;
      if (startRow < 0) {
        return false;
      }
      setRowData((prev) => {
        const next = [...prev];
        values.forEach((rowValues, rowOffset) => {
          const rowIndex = startRow + rowOffset;
          const row = next[rowIndex];
          if (!row) {
            return;
          }
          rowValues.forEach((cellValue, colOffset) => {
            const colIndex = startCol + colOffset;
            if (colIndex < 4) {
              return;
            }
            const yearIndex = colIndex - 4;
            const year = years[yearIndex];
            if (!year) {
              return;
            }
            const parsed = parsePastedValue(cellValue);
            if (parsed === undefined) {
              return;
            }
            row.budgetByYear[year] = parsed;
            handleBudgetChange(row.accountId, year, parsed);
          });
        });
        return next;
      });
      return false;
    },
    [handleBudgetChange, houseRole, years],
  );

  const handleCellClicked = useCallback(
    (cell: Item) => {
      const [col, row] = cell;
      const flipColumnIndex = 4 + years.length;
      if (col !== flipColumnIndex || houseRole === "readonly") {
        return;
      }
      setRowData((prev) => {
        const next = [...prev];
        const record = next[row];
        if (!record) {
          return prev;
        }
        const nextBudgets: Record<number, number | null> = { ...record.budgetByYear };
        years.forEach((year) => {
          const value = nextBudgets[year];
          if (value === null || value === undefined) {
            return;
          }
          const inverted = -value;
          nextBudgets[year] = inverted;
          handleBudgetChange(record.accountId, year, inverted);
        });
        next[row] = { ...record, budgetByYear: nextBudgets };
        return next;
      });
    },
    [handleBudgetChange, houseRole, years],
  );

  const copyAllAsTsv = async () => {
    if (!report) {
      return;
    }
    const header = [
      t("accounts.account", "Account"),
      t("accounts.name", "Name"),
      `${t("reports.actual", "Actual")} ${actualYear}`,
      ...years.map((year) => String(year)),
    ].join("\t");
    const lines = rowData.map((row) => {
      const values = years.map((year) => {
        const value = row.budgetByYear[year] ?? null;
        return value === null ? "" : String(value);
      });
      return [row.number, row.name, String(row.actual ?? 0), ...values].join("\t");
    });
    const payload = [header, ...lines].join("\n");
    try {
      await navigator.clipboard.writeText(payload);
      setNotice(t("reports.copyTsvSuccess", "Copied table to clipboard."));
    } catch {
      setNotice(t("reports.copyTsvFailed", "Failed to copy to clipboard."));
    }
  };

  const saveChanges = async () => {
    if (!report || dirtyKeys.size === 0 || !houseId) {
      return;
    }
    setError(null);
    setNotice(null);
    const rowsById = new Map(rowData.map((row) => [row.accountId, row]));
    const updates = Array.from(dirtyKeys).map((key) => {
      const [accountIdRaw, yearRaw] = key.split(":");
      const accountId = Number.parseInt(accountIdRaw, 10);
      const year = Number.parseInt(yearRaw, 10);
      const row = rowsById.get(accountId);
      const amount = row?.budgetByYear?.[year] ?? null;
      return {
        accountId,
        year,
        amount,
      };
    });
    setIsSaving(true);
    try {
      await fetchJson(`/api/houses/${houseId}/budget-worksheet`, {
        method: "PUT",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ startYear, updates }),
      });
      setNotice(t("reports.budgetWorksheetSaved", "Budgets saved."));
      const refreshed = await fetchJson<BudgetWorksheetResponse>(
        `/api/houses/${houseId}/budget-worksheet?startYear=${startYear}&actualYear=${actualYear}`,
      );
      setReport(refreshed);
    } catch (saveError) {
      setError(
        saveError instanceof Error
          ? saveError.message
          : t("reports.budgetWorksheetSaveFailed", "Failed to save budgets."),
      );
    } finally {
      setIsSaving(false);
    }
  };

  if (!houseId) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t("reports.budgetWorksheetTitle", "Budget worksheet")}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted">
            {t("reports.budgetWorksheetSelectHouse", "Select a house to edit budgets.")}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-lg font-semibold text-ink">
            {t("reports.budgetWorksheetTitle", "Budget worksheet")}
          </p>
          <p className="text-sm text-muted">
            {t(
              "reports.budgetWorksheetSubtitle",
              "Edit five years of budgets and copy/paste with Excel.",
            )}
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-4">
          <div className="space-y-1">
            <label className="inline-block text-sm font-medium text-ink" htmlFor="budget-start-year">
              {t("reports.budgetStartYear", "Start year")}
            </label>
            <Input
              id="budget-start-year"
              type="number"
              value={String(startYear)}
              onChange={(event) => updateStartYear(event.target.value)}
              className="h-9 w-28"
              min={2000}
              max={2100}
            />
          </div>
          <div className="space-y-1">
            <label className="inline-block text-sm font-medium text-ink" htmlFor="budget-actual-year">
              {t("reports.actualYear", "Actual year")}
            </label>
            <Input
              id="budget-actual-year"
              type="number"
              value={String(actualYear)}
              onChange={(event) => updateActualYear(event.target.value)}
              className="h-9 w-28"
              min={2000}
              max={2100}
            />
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={saveChanges}
              disabled={dirtyKeys.size === 0 || isSaving || houseRole === "readonly"}
            >
              {isSaving ? t("reports.savingBudget", "Saving...") : t("reports.saveBudget", "Save")}
            </Button>
          </div>
        </div>
      </div>
      {isLoading ? <p className="text-sm text-muted">{t("reports.loading", "Loading...")}</p> : null}
      {error ? <p className="text-sm text-error">{error}</p> : null}
      {notice ? <p className="text-sm text-muted">{notice}</p> : null}
      {houseRole === "readonly" ? (
        <p className="text-sm text-muted">
          {t("reports.budgetReadonly", "Budgets are read-only for this house.")}
        </p>
      ) : null}
      <Card>
        <CardHeader>
          <CardTitle>{t("reports.budgetWorksheetTableTitle", "Budget grid")}</CardTitle>
        </CardHeader>
        <CardContent>
          {!report ? (
            <p className="text-sm text-muted">{t("reports.noData", "No report data.")}</p>
          ) : report.rows.length === 0 ? (
            <p className="text-sm text-muted">
              {t("reports.noProfitLossAccounts", "No profit & loss accounts configured yet.")}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <div style={{ width: "100%", minWidth: "760px", height: "720px" }}>
                <DataEditor
                  width="100%"
                  height={720}
                  rowHeight={BUDGET_GRID_ROW_HEIGHT}
                  headerHeight={BUDGET_GRID_HEADER_HEIGHT}
                  columns={columns}
                  rows={rowData.length}
                  getCellContent={getCellContent}
                  onCellEdited={handleCellEdited}
                  onCellClicked={handleCellClicked}
                  getCellsForSelection={true}
                  onPaste={handlePaste}
                  rangeSelect="rect"
                  rowMarkers="none"
                />
              </div>
              <div className="mt-4 flex justify-end">
                <Button variant="outline" onClick={copyAllAsTsv} aria-label={t("reports.copyTsv", "Copy as TSV")}>
                  <svg
                    aria-hidden="true"
                    focusable="false"
                    viewBox="0 0 24 24"
                    className="h-4 w-4"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={1.5}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <rect x="9" y="9" width="13" height="13" rx="2" />
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                  </svg>
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
      <p className="text-right text-xs text-muted">
        {t("reports.amountsInCurrency", "Amounts in {currency}.", { currency: currencyLabel })}
      </p>
    </div>
  );
};
